import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import * as programsSelectors from '@makemydeal/dr-dash-store/selectors/programsSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';

import { NON_MANUAL_INCENTIVE_DISPLAY_TYPES } from '@makemydeal/dr-dash-types';
import { utils as offerReduxUtils } from '@makemydeal/dr-offer-redux';
import { findExcludingIncentiveId, shouldExcludeIncentive } from './utils';
import { getIncentiveDisplayType } from '../IncentivesCard/utils';

const getOverriddenIncentiveCodes = offerReduxUtils.incentives.getOverriddenIncentiveCodes;

const buildIncentiveNameById = (...lists: { incentiveId: number; programName?: string }[][]): Map<number, string> => {
    const names = new Map<number, string>();
    lists.flat().forEach((incentive) => {
        if (incentive.programName) names.set(Number(incentive.incentiveId), incentive.programName);
    });
    return names;
};

export const useNestedIncentivesTableData = () => {
    const isProgramIncentivesRestorationEnabled = useSelector(featureToggleSelectors.isProgramIncentivesRestorationEnabled);
    const conditionals = useSelector(programsSelectors.getAvailableConditionalIncentives);
    const nonConditionals = useSelector(programsSelectors.getAppliedNonConditionalIncentives);
    const availableNonConditionals = useSelector(programsSelectors.getAvailableNonConditionalIncentives);
    const appliedConditionals = useSelector(programsSelectors.getAppliedConditionalIncentives);
    const appliedProgramIncentiveIds = useSelector(programsSelectors.getAppliedIncentiveIdsForCurrentOffer);
    const incentiveExclusionsById = useSelector(programsSelectors.getincentiveExclusionsById);
    const programLossDisabledIds = useSelector(programsSelectors.getProgramLossDisabledIds);
    const compatibleIncentiveIds = useSelector(programsSelectors.getSelectedTermCompatibleIncentiveIds);
    const appliedIncentives = useSelector(offerReduxSelectors.getAppliedIncentives);
    const isZeroAmountSelectionDisabled = useSelector(featureToggleSelectors.isZeroAmountIncentiveSelectionDisabled);
    const keepsAppliedZeroRows = useSelector((state: any) =>
        offerReduxUtils.incentives.isGridIncentiveManagementEnabled(state, offerReduxSelectors.getCurrentOfferType(state))
    );

    const nestedIncentivesTableData = useMemo(() => {
        if (!isProgramIncentivesRestorationEnabled) {
            return [...conditionals, ...nonConditionals].map((incentive) => {
                const selected = appliedIncentives.some((applied) => {
                    const appliedDisplayType = getIncentiveDisplayType(applied, false);
                    return (
                        applied.code === incentive.incentiveId &&
                        (appliedDisplayType === NON_MANUAL_INCENTIVE_DISPLAY_TYPES.manufacturer ||
                            appliedDisplayType === NON_MANUAL_INCENTIVE_DISPLAY_TYPES.included)
                    );
                });
                const isExcluded = shouldExcludeIncentive(incentive.incentiveId, incentiveExclusionsById, undefined);

                return {
                    ...incentive,
                    key: incentive.incentiveId,
                    displayType: incentive.isStandardIncentive
                        ? NON_MANUAL_INCENTIVE_DISPLAY_TYPES.manufacturer
                        : NON_MANUAL_INCENTIVE_DISPLAY_TYPES.included,
                    selected,
                    isExcluded
                };
            });
        }

        const overriddenIncentiveCodes = getOverriddenIncentiveCodes(appliedIncentives);
        const programBackedCodes = new Set(
            appliedIncentives.filter((applied) => applied.manual !== true).map((applied) => Number(applied.code))
        );
        const isCustomOnly = (id: number): boolean => overriddenIncentiveCodes.has(id) && !programBackedCodes.has(id);
        const exclusionSourceIds = appliedProgramIncentiveIds.filter((id) => !isCustomOnly(Number(id)));
        const incentiveNameById = buildIncentiveNameById(conditionals, appliedConditionals, nonConditionals);
        // One lookup answers both "is this row blocked" and "by what", so the two cannot disagree.
        const findBlocker = (incentiveId: number, isSelected: boolean): number | undefined =>
            isSelected ? undefined : findExcludingIncentiveId(incentiveId, incentiveExclusionsById, exclusionSourceIds);

        const nonConditionalRows = availableNonConditionals.map((incentive) => {
            const isOverridden = overriddenIncentiveCodes.has(Number(incentive.incentiveId));
            const isApplied = appliedProgramIncentiveIds.includes(incentive.incentiveId);
            const blockerId = findBlocker(incentive.incentiveId, isApplied);
            return {
                ...incentive,
                key: incentive.incentiveId,
                displayType: NON_MANUAL_INCENTIVE_DISPLAY_TYPES.manufacturer,
                selected: isApplied,
                isExcluded: blockerId !== undefined,
                blockingIncentiveName: blockerId === undefined ? undefined : incentiveNameById.get(blockerId),
                isProgramLossDisabled: false,
                isOverridden
            };
        });

        const conditionalRows = conditionals.map((incentive) => {
            const selected = appliedProgramIncentiveIds.includes(incentive.incentiveId);
            const isOverridden = overriddenIncentiveCodes.has(Number(incentive.incentiveId));
            const blockerId = findBlocker(incentive.incentiveId, selected);
            const isProgramLossDisabled =
                !selected &&
                !(compatibleIncentiveIds ?? []).includes(incentive.incentiveId) &&
                programLossDisabledIds.includes(incentive.incentiveId);

            return {
                ...incentive,
                key: incentive.incentiveId,
                displayType: incentive.isStandardIncentive
                    ? NON_MANUAL_INCENTIVE_DISPLAY_TYPES.manufacturer
                    : NON_MANUAL_INCENTIVE_DISPLAY_TYPES.conditional,
                selected: !isProgramLossDisabled && selected,
                isExcluded: blockerId !== undefined,
                blockingIncentiveName: blockerId === undefined ? undefined : incentiveNameById.get(blockerId),
                isProgramLossDisabled,
                isOverridden
            };
        });

        const rows = [...nonConditionalRows, ...conditionalRows];

        if (!isZeroAmountSelectionDisabled) {
            return rows;
        }

        const isUntickedZeroRow = (row: (typeof rows)[number]): boolean =>
            row.cash === 0 && !(keepsAppliedZeroRows && appliedProgramIncentiveIds.includes(row.incentiveId));
        const zeroAmountRows = rows.filter(isUntickedZeroRow).map((row) => ({ ...row, selected: false }));
        return [...rows.filter((row) => !isUntickedZeroRow(row)), ...zeroAmountRows];
    }, [
        isProgramIncentivesRestorationEnabled,
        conditionals,
        nonConditionals,
        availableNonConditionals,
        appliedConditionals,
        appliedProgramIncentiveIds,
        incentiveExclusionsById,
        programLossDisabledIds,
        compatibleIncentiveIds,
        appliedIncentives,
        isZeroAmountSelectionDisabled,
        keepsAppliedZeroRows
    ]);

    return { nestedIncentivesTableData };
};
